import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    // Free-entry mode: lets the visitor type values that are not in the option
    // list (the wish form's skills/industries have no canonical list to offer).
    // Off by default — the job form's selects must stay restricted to their
    // collections.
    create: Boolean,
    createLabel: String,
    // { "<option value>": "бухгалтер облік …" } — a hidden search index, never displayed. Absent on
    // every select but the job form's specializations, where the vocabulary is English and the
    // page is Ukrainian.
    synonyms: Object
  }

  async connect () {
    const { default: TomSelect } = await import('tom-select')
    const blankOption = this.element.querySelector('option[value=""]')
    this.#indexSynonyms()
    this.selectInstance = new TomSelect(this.element, {
      plugins: ['remove_button', 'clear_button'],
      hidePlaceholder: true,
      // TomSelect stops after 50 options of its own accord. The specialization vocabulary is 177
      // tags, so the list ended two thirds of the way through with nothing to say that it had —
      // and which 50 you got was Postgres's row order. Null is TomSelect's "no limit".
      maxOptions: null,
      // Makes the `syn` field #indexSynonyms writes searchable alongside the visible title.
      // Harmless on a select that carries no index — sifter simply finds no such field.
      searchField: ['text', 'syn'],
      create: this.createValue,
      // Without this a tag that was typed but not confirmed with Enter is
      // silently discarded the moment the visitor clicks the submit button.
      createOnBlur: this.createValue,
      // TomSelect only reads `placeholder` off the element attribute, which a
      // <select> does not have — surface the include_blank prompt instead.
      placeholder: blankOption?.textContent || undefined,
      render: this.createValue
        ? {
            option_create: (data, escape) =>
              `<div class="create">${escape(this.createLabelValue || 'Add')} <strong>${escape(data.input)}</strong>&hellip;</div>`
          }
        : {}
    })
    // setValues may have been called before this async import resolved; apply now.
    if (this.pendingValues) {
      this.selectInstance.setValue(this.pendingValues, false)
      this.pendingValues = null
    }
  }

  // Writes each option's synonyms onto the option as `data-syn`, BEFORE TomSelect is constructed —
  // it copies the whole of `option.dataset` onto its own option object once, at construction
  // (`getSettings.js`, `readData`), so anything written afterwards is never seen.
  //
  // A plain `data-syn` rather than the `dataAttr` setting on purpose: that setting indexes the
  // dataset by the raw attribute name in this build, so `dataAttr: 'data-data'` silently finds
  // nothing. The dataset copy above is what both builds agree on.
  //
  // The words go nowhere near the option's text: an employer must read "Accounting", not
  // "Accounting бухгалтер облік".
  #indexSynonyms () {
    if (!this.hasSynonymsValue) return

    for (const option of this.element.options) {
      const words = this.synonymsValue[option.value]

      if (words) option.dataset.syn = words
    }
  }

  setValues (ids) {
    if (!Array.isArray(ids)) return

    const values = ids.map(String)
    if (this.selectInstance) {
      this.selectInstance.setValue(values, false)
    } else {
      // TomSelect hasn't mounted yet — apply once connect() finishes.
      this.pendingValues = values
    }
  }

  disconnect () {
    this.selectInstance?.destroy()
  }
}
